import { Injectable } from '@angular/core';
import { NilSorting, SortDirection } from '@ddt-web/common';
import { CellData, TableData } from '@ddt-web/data-service';
import * as get from 'lodash/get';
import * as isNil from 'lodash/isNil';

@Injectable({
	providedIn: 'root'
})
export class TableSortService {
	/**
	 * Sorts the provided array (format of [key: string]: CellData) using the provided sort key and sort direction and returns the result.
	 * If sortDirection is 0 or the data array is null or undefined, the provided data array is returned.
	 *
	 * @param dataArrayToSort array to be sorted in the format [key: string]: CellData
	 * @param sortKey the key used to sort the data array (id of the column to be sorted)
	 * @param sortDirection the direction of sorting (0 unsorted, 1 ascending, -1 descending)
	 * @param nilSorting determines whether nil should be considered the smallest/largest value or should be the last value regardless of sort direction
	 * @returns the sorted array
	 */
	public sortDataArray(dataArrayToSort: Array<{ [key: string]: CellData }>, sortKey: string, sortDirection: SortDirection, nilSorting: NilSorting = NilSorting.LAST):
	Array<{ [key: string]: CellData }> {
		if (sortDirection === SortDirection.UNSORTED || isNil(dataArrayToSort)) {
			return dataArrayToSort;
		}

		return dataArrayToSort.sort((row1, row2) => {
			const row1ToSort: CellData = get(row1, `[${sortKey}]`);
			const row2ToSort: CellData = get(row2, `[${sortKey}]`);

			return this.sortCellData(row1ToSort, row2ToSort, sortDirection, nilSorting);
		});
	}

	/**
	 * Sorts the provided table data object using the provided sort key and sort direction and returns the result.
	 * If sortDirection is 0 or table data is null or undefined or table data has no cells, the provided table data is returned.
	 *
	 * @param tableDataToSort table data object to be sorted
	 * @param sortKey the key used to sort the table data (id of the column to be sorted)
	 * @param sortDirection the direction of sorting (0 unsorted, 1 ascending, -1 descending)
	 * @param nilSorting determines whether nil should be considered the smallest/largest value or should be the last value regardless of sort direction
	 * @returns the sorted table data
	 */
	public sortTableData(tableDataToSort: TableData, sortKey: string, sortDirection: number, nilSorting: NilSorting = NilSorting.LAST): TableData {
		if (sortDirection === SortDirection.UNSORTED || isNil(tableDataToSort) || isNil(tableDataToSort.cells)) {
			return tableDataToSort;
		}

		const cellDataArray: CellData[][] = tableDataToSort.cells;
		cellDataArray.sort((row1: CellData[], row2: CellData[]) => {
			const row1ToSort: CellData = row1.find(cell => cell.config.id === sortKey);
			const row2ToSort: CellData = row2.find(cell => cell.config.id === sortKey);

			return this.sortCellData(row1ToSort, row2ToSort, sortDirection, nilSorting);
		});

		return new TableData(
			tableDataToSort.headersX,
			tableDataToSort.headersY,
			cellDataArray,
			tableDataToSort.portfolioData,
			tableDataToSort.firstColumnTitle,
			tableDataToSort.transposed
		);
	}

	/**
	 * Sorts the provided 2 cell data objects using the provided sort direction and returns the result.
	 *
	 * @param row1 CellData object
	 * @param row2 CellData object
	 * @param sortDirection the direction of sorting (0 unsorted, 1 ascending, -1 descending)
	 * @param nilSorting determines whether nil should be considered the smallest/largest value or should be the last value regardless of sort direction
	 * @returns number
	 */
	public sortCellData(row1: CellData, row2: CellData, sortDirection: SortDirection, nilSorting: NilSorting = NilSorting.LAST): number {
		const rawVal1 = get(row1, 'rawValue') as any;
		const rawVal2 = get(row2, 'rawValue') as any;
		const formattedVal1 = get(row1, 'formattedValue') as any;
		const formattedVal2 = get(row2, 'formattedValue') as any;

		if (formattedVal1 === 'n/a' && formattedVal2 === 'n/a') {
			return 0;
		}

		const firstNilAndSecondNonNil: boolean = isNil(rawVal1) && !isNil(rawVal2);
		const fistNonNilAndSecondNil: boolean = !isNil(rawVal1) && isNil(rawVal2);

		switch (nilSorting) {
			case NilSorting.LAST: {
				if (firstNilAndSecondNonNil) {
					return 1;
				}
				if (fistNonNilAndSecondNil) {
					return -1;
				}
				break;
			}
			case NilSorting.SMALLEST: {
				if (firstNilAndSecondNonNil) {
					return sortDirection === SortDirection.ASC ? -1 : 1;
				}

				if (fistNonNilAndSecondNil) {
					return sortDirection === SortDirection.ASC ? 1 : -1;
				}
				break;
			}
			case NilSorting.LARGEST: {
				if (firstNilAndSecondNonNil) {
					return sortDirection === SortDirection.ASC ? 1 : -1;
				}
				if (fistNonNilAndSecondNil) {
					return sortDirection === SortDirection.ASC ? -1 : 1;
				}
				break;
			}
		}

		if (typeof rawVal1 === 'string' || typeof rawVal2 === 'string') {
			return sortDirection === SortDirection.ASC ? rawVal1.toString().localeCompare(rawVal2.toString()) : rawVal2.toString().localeCompare(rawVal1.toString());
		}

		return sortDirection === SortDirection.ASC ? rawVal1 - rawVal2 : rawVal2 - rawVal1;
	}
}
